import { useRef, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { ChevronDown, ChevronUp, BarChart3 } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, Tooltip, Legend);

// ── Custom plugin: green zone + teal avg line + diamond markers ───────────────
const meridianAnnotationsPlugin = {
  id: 'meridianAnnotations',
  beforeDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;
    const opts = chart.options.plugins?.meridianAnnotations;
    if (!opts) return;

    const { p25BucketIdx } = opts;
    if (p25BucketIdx == null) return;

    // Green zone: fill from left edge to end of p25 bucket
    const xStart = chartArea.left;
    const xEnd = scales.x.getPixelForValue(p25BucketIdx) +
      (scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0)) / 2;

    ctx.save();
    ctx.fillStyle = 'rgba(16, 185, 129, 0.10)';
    ctx.fillRect(xStart, chartArea.top, Math.max(0, xEnd - xStart), chartArea.bottom - chartArea.top);
    ctx.restore();
  },
  afterDatasetsDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;
    const opts = chart.options.plugins?.meridianAnnotations;
    if (!opts) return;

    const { avgBucketIdx, namedPoints, buckets, p25BucketIdx } = opts;
    const barW = scales.x.getPixelForValue(1) - scales.x.getPixelForValue(0);

    // "Best value" label
    if (p25BucketIdx != null) {
      const labelX = chartArea.left + 6;
      ctx.save();
      ctx.fillStyle = 'rgba(16, 185, 129, 0.9)';
      ctx.font = '10px "Outfit", sans-serif';
      ctx.fillText('Best value range', labelX, chartArea.top + 14);
      ctx.restore();
    }

    // Regional average vertical dashed line
    if (avgBucketIdx != null) {
      const avgX = scales.x.getPixelForValue(avgBucketIdx);
      ctx.save();
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = '#0D3D4A';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(avgX, chartArea.top);
      ctx.lineTo(avgX, chartArea.bottom);
      ctx.stroke();
      ctx.setLineDash([]);
      // Label
      ctx.fillStyle = '#0D3D4A';
      ctx.font = 'bold 10px "Outfit", sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Reg. avg', avgX + 4, chartArea.top + 13);
      ctx.restore();
    }

    // Diamond markers for named facilities
    if (namedPoints && buckets) {
      for (const pt of namedPoints.slice(0, 12)) {
        const bIdx = findBucketIdx(pt.price, buckets);
        if (bIdx < 0) continue;
        const x = scales.x.getPixelForValue(bIdx);
        const y = scales.y.getPixelForValue(0) + 10;

        // Draw rotated square (diamond)
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(Math.PI / 4);
        ctx.fillStyle = '#0D3D4A';
        ctx.fillRect(-5, -5, 10, 10);
        ctx.restore();
      }
    }
  },
};

function findBucketIdx(price, buckets) {
  for (let i = 0; i < buckets.length; i++) {
    if (price >= buckets[i].min && price < buckets[i].max) return i;
    if (i === buckets.length - 1 && price >= buckets[i].min) return i;
  }
  return -1;
}

export default function PriceDistributionChart({ data, title, footnote, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const chartRef = useRef(null);

  if (!data || !data.buckets || data.buckets.length === 0) return null;

  const { buckets, regional_average, p25, named_points, total_count } = data;
  const avgBucketIdx = findBucketIdx(regional_average, buckets);
  const p25BucketIdx = findBucketIdx(p25, buckets);

  const chartData = {
    labels: buckets.map(b => b.label),
    datasets: [
      {
        label: 'Facilities',
        data: buckets.map(b => b.count),
        backgroundColor: '#A8B4B8',
        borderColor: '#8fa0a6',
        borderWidth: 1,
        borderRadius: 2,
        hoverBackgroundColor: '#8fa0a6',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => items[0].label,
          label: (item) => `${item.raw} facilities`,
        },
        backgroundColor: '#0D3D4A',
        titleColor: '#F0EEE9',
        bodyColor: '#F0EEE9',
        padding: 10,
        cornerRadius: 0,
      },
      meridianAnnotations: {
        avgBucketIdx,
        p25BucketIdx,
        namedPoints: named_points,
        buckets,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: '#A8B4B8',
          font: { size: 10, family: '"Outfit", sans-serif' },
          maxRotation: 30,
        },
        border: { color: '#E7E5E0' },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(168,180,184,0.15)' },
        ticks: {
          color: '#A8B4B8',
          font: { size: 10 },
          maxTicksLimit: 6,
        },
        title: {
          display: true,
          text: 'Facilities',
          color: '#A8B4B8',
          font: { size: 10 },
        },
        border: { color: '#E7E5E0' },
      },
    },
    animation: { duration: 400 },
  };

  return (
    <div className="bg-white border border-stone-200 mb-6" data-testid="price-distribution-chart">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <BarChart3 size={14} className="text-[#0D3D4A]" />
          <h2 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {title}
          </h2>
        </div>
        <div className="flex items-center gap-4">
          {regional_average && (
            <span className="text-xs text-[#A8B4B8]">
              Avg <strong className="text-[#0D3D4A]">${Math.round(regional_average).toLocaleString()}</strong>
              {p25 && <span className="ml-2">· Best value &lt; <strong className="text-emerald-600">${Math.round(p25).toLocaleString()}</strong></span>}
            </span>
          )}
          <button
            onClick={() => setOpen(!open)}
            className="text-[#A8B4B8] hover:text-[#0D3D4A] transition-colors"
            data-testid="chart-toggle"
          >
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Chart body */}
      {open && (
        <div className="px-6 py-4">
          <div style={{ height: 200 }}>
            <Bar
              ref={chartRef}
              data={chartData}
              options={chartOptions}
              plugins={[meridianAnnotationsPlugin]}
            />
          </div>

          {/* Named facility markers legend */}
          {named_points && named_points.length > 0 && (
            <div className="mt-4 border-t border-stone-100 pt-3">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-2">
                Facilities shown (lowest prices)
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {named_points.slice(0, 10).map((pt, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-stone-600">
                    <span className="inline-block w-2 h-2 bg-[#0D3D4A] rotate-45 flex-shrink-0" />
                    {pt.name} —{' '}
                    <strong className="text-[#0D3D4A]">${pt.price.toLocaleString()}</strong>
                  </div>
                ))}
              </div>
              {footnote && (
                <p className="mt-2 text-xs text-stone-400 italic">
                  {footnote}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
